import { Field, Form, Formik, FormikHelpers } from 'formik'
import { FC, ReactElement, useEffect, useMemo } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import * as yup from 'yup'
import Button from '../../components/button'
import H3 from '../../components/h3'
import Input from '../../components/input'
import InputRadio from '../../components/inputRadio'
import { useAuth } from '../../hooks/auth'
import { usePatients } from '../../hooks/patients'
import { RoutesEnum } from '../../utils/routesEnum'
import ModalImcCalculator from '../imcCalculator/modal'
import VerifyPhone from '../verifyPhone'
import { Container } from './style'

const schema = yup.object().shape({
  name: yup.string().min(3, 'Nome inválido')
    .required('Nome obrigatório'),
  email: yup.string().email('Email inválido'),
  age: yup.number().when('processType', {
    is: 'empty',
    then: yup.number().typeError('Idade Inválida').required('Idade obrigatória'),
    otherwise: yup.number().typeError('Idade Inválida').nullable()
  }),
  imc: yup.number().when('processType', {
    is: 'empty',
    then: yup.number().typeError('IMC inválido').required('IMC obrigatório'),
    otherwise: yup.number().typeError('IMC inválido').nullable()
  }),
  sex: yup.string().required('Sexo obrigatório'),
  pregnant: yup.string().when('sex', {
    is: 'female',
    then: yup.string().required('Item obrigatório'),
    otherwise: yup.string().nullable()
  }),
  hasMenopause: yup.string().when('sex', {
    is: 'female',
    then: yup.string().required('Item obrigatório'),
    otherwise: yup.string().nullable()
  })
})

const initialValuesContent = {
  name: '',
  email: '',
  age: '',
  imc: '',
  sex: '',
  pregnant: undefined,
  hasMenopause: undefined
}

const SavePatient: FC = (): ReactElement => {
  const { create, patient, getOne, edit } = usePatients()
  const { user } = useAuth()
  const history = useHistory()
  const { patientId } = useParams<{ patientId: string }>()

  useEffect(() => {
    if (patientId) {
      getOne(patientId)
    }
  }, [patientId, getOne])

  const initialValues = useMemo(() => {
    if (patientId) return patient
    return initialValuesContent
  }, [patientId])

  const submit = async (values: typeof initialValues, helpers: FormikHelpers<typeof initialValues>): Promise<void> => {
    if (!user?.isVerified) {
      history.push({ search: '?verifyPhone=1' })
      return
    }

    helpers.setSubmitting(true)
    try {
      if (patientId) {
        await edit({
          _id: patientId,
          ...values,
          age: Number(values.age),
          imc: Number(values.imc),
          sex: values.sex as 'male' | 'female'
        })
        history.push(RoutesEnum.PATIENTS_VIEW.replace(':patientId', patientId))
      } else {
        await create({
          ...values,
          age: Number(values.age),
          imc: Number(values.imc),
          sex: values.sex as 'male' | 'female'
        })
        history.push(RoutesEnum.PATIENTS)
      }
    } catch { }
    helpers.setSubmitting(false)
  }

  return (
    <Container>
      <H3>
        Dados do paciente
      </H3>
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={submit}
      >
        {({ setFieldValue, values, isSubmitting, isValid }) => (
          <Form>
            <Field
              label='Nome do paciente'
              name='name'
              component={Input}
            />
            <Field
              label='Email do paciente'
              name='email'
              type='emial'
              component={Input}
            />
            <Field
              label='Idade do paciente'
              name='age'
              type='tel'
              maxLength={3}
              component={Input}
            />
            <Field
              label='IMC do paciente'
              name='imc'
              type='tel'
              maxLength={3}
              component={Input}
              info={<Link to='?imcCalc=true'>Calculadora IMC</Link>}
            />
            <Field
              label='Sexo do paciente'
              name='sex'
              options={[
                { name: 'Masculino', value: 'male' },
                { name: 'Feminino', value: 'female' }
              ]}
              component={InputRadio}
            />
            {values.sex === 'female' && (
              <>
                <Field
                  label='Gravidez'
                  name='pregnant'
                  options={[
                    { name: 'Grávida', value: 'pregnant' },
                    { name: 'Não grávida', value: 'not-pregnant' }
                  ]}
                  component={InputRadio}
                />
                <Field
                  label='Menopausa'
                  name='hasMenopause'
                  options={[
                    { name: 'Menopausa', value: 'menopause' },
                    { name: 'Não menopausa', value: 'not-menopause' }
                  ]}
                  component={InputRadio}
                />
              </>
            )}
            <Button
              disabled={!isValid}
              isLoading={isSubmitting}
              type='submit'
              block
            >
              {patientId ? 'EDITAR PACIENTE' : 'CADASTRAR PACIENTE'}
            </Button>
            <ModalImcCalculator
              setImc={(val) => setFieldValue('imc', val)}
            />
          </Form>
        )}
      </Formik>
      <VerifyPhone />
    </Container>
  )
}

export default SavePatient
